import { Box, Typography } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { usePrivacyMode } from "hooks/usePrivacyMode";

// Mobile tap-reveal auto re-blur delay (Privacy Mode).
const MOBILE_REVEAL_MS = 10000;

/**
 * Hourly rate value cell.
 *
 * Privacy Mode aware: when the device-local privacy mode is ON, the value
 * is blurred by default and sits inside a grey pill (background, radius,
 * padding). Hovering or focusing the cell reveals the value; when the
 * pointer leaves or focus is lost, the value blurs again.
 * When privacy mode is OFF, the value is fully visible with no blur and
 * no pill chrome — just the plain number.
 *
 * Mobile (<600px): there is no hover, so a tap reveals the value and it
 * auto re-blurs after 10 seconds. Tapping again while revealed re-blurs
 * immediately; the next tap reveals again. The tap is swallowed
 * (stopPropagation) so it never triggers DataGrid row selection/navigation.
 *
 * Props:
 *  - amount: numeric value; formatted as "$X.XX" when `value` is not given.
 *  - value:  optional pre-formatted display string (e.g. "$1,234.56").
 *  - variant: Typography variant (default "body2").
 */
const HourlyRateHoverCell = ({ amount, value, variant = "body2" }) => {
  const theme = useTheme();
  const { privacyMode } = usePrivacyMode();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const [revealed, setRevealed] = useState(false);
  const revealTimerRef = useRef(null);
  const safeAmount = Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "$0.00";
  const display = value !== undefined && value !== null ? value : safeAmount;

  const clearRevealTimer = () => {
    if (revealTimerRef.current) {
      clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  };

  // Never leave a pending auto-re-blur timer behind (DataGrid virtualizes rows).
  useEffect(() => {
    return () => {
      if (revealTimerRef.current) {
        clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, []);

  // Mobile: tap toggles the reveal (auto re-blur after 10s). The tap never
  // bubbles to the DataGrid row — no row selection/navigation from the cell.
  const handleTap = (event) => {
    if (!privacyMode) return; // nothing hidden → keep the row tap behaviour
    event.stopPropagation();

    if (revealed) {
      clearRevealTimer();
      setRevealed(false);
      return;
    }

    clearRevealTimer();
    setRevealed(true);
    revealTimerRef.current = setTimeout(() => {
      revealTimerRef.current = null;
      setRevealed(false);
    }, MOBILE_REVEAL_MS);
  };

  // Privacy Mode ON: blurred by default; hover/focus reveals, blur returns
  // when the pointer leaves / focus is lost. OFF: always plain.
  // On mobile the reveal is tap-driven only (hover/focus handlers are desktop).
  const isBlurred = privacyMode && !revealed;

  return (
    <Box
      component="span"
      tabIndex={0}
      onMouseEnter={isMobile ? undefined : () => setRevealed(true)}
      onMouseLeave={isMobile ? undefined : () => setRevealed(false)}
      onFocus={isMobile ? undefined : () => setRevealed(true)}
      onBlur={isMobile ? undefined : () => setRevealed(false)}
      onClick={isMobile ? handleTap : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        // Grey pill chrome only in Privacy Mode — off means plain number.
        ...(privacyMode
          ? {
              borderRadius: "8px",
              px: 0.75,
              py: 0.25,
              backgroundColor: theme.palette.action.hover,
              cursor: "default",
              outline: "none",
            }
          : {}),
        "& .hourly-rate-value": {
          filter: isBlurred ? "blur(6px)" : "none",
          userSelect: isBlurred ? "none" : "text",
          transition: "filter 0.2s ease",
        },
      }}
    >
      <Typography component="span" variant={variant} className="hourly-rate-value">
        {display}
      </Typography>
    </Box>
  );
};

export default HourlyRateHoverCell;
